// src/components/ui/NotificationSystem.tsx
import React, { useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { FiX, FiCheckCircle, FiXCircle, FiAlertTriangle, FiInfo } from 'react-icons/fi';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { selectNotifications } from '../../store/selectors';
import { removeNotification } from '../../store/slices/uiSlice';

const NotificationSystem: React.FC = () => {
	const dispatch = useAppDispatch();
	const notifications = useAppSelector(selectNotifications);

	// Auto-remove notifications after their duration
	useEffect(() => {
		notifications.forEach((notification) => {
			if (notification.duration) {
				const timer = setTimeout(() => {
					dispatch(removeNotification(notification.id));
				}, notification.duration);

				return () => clearTimeout(timer);
			}
		});
	}, [notifications, dispatch]);

	const getIcon = (type: string) => {
		switch (type) {
			case 'success':
				return <FiCheckCircle className="w-5 h-5 text-green-500" />;
			case 'error':
				return <FiXCircle className="w-5 h-5 text-red-500" />;
			case 'warning':
				return <FiAlertTriangle className="w-5 h-5 text-yellow-500" />;
			case 'info':
			default:
				return <FiInfo className="w-5 h-5 text-blue-500" />;
		}
	};

	const getBackgroundColor = (type: string) => {
		switch (type) {
			case 'success':
				return 'bg-green-50 border-green-200 dark:bg-green-900/90 dark:border-green-800';
			case 'error':
				return 'bg-red-50 border-red-200 dark:bg-red-900/90 dark:border-red-800';
			case 'warning':
				return 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/90 dark:border-yellow-800';
			case 'info':
			default:
				return 'bg-blue-50 border-blue-200 dark:bg-blue-900/90 dark:border-blue-800';
		}
	};

	if (notifications.length === 0) {
		return null;
	}

	return (
		<div className="fixed top-4 end-1/2 translate-x-1/2 z-50 space-y-2 max-w-sm">
			<AnimatePresence>
				{notifications.map((notification) => (
					<motion.div
						key={notification.id}
						initial={{ opacity: 0, y: -50, scale: 0.95 }}
						animate={{ opacity: 1, y: 0, scale: 1 }}
						exit={{ opacity: 0, y: -50, scale: 0.95 }}
						transition={{ duration: 0.3 }}
						className={`relative rounded-lg border p-4 shadow-lg ${getBackgroundColor(notification.type)}`}
					>
						<div className="flex items-start gap-3">
							<div className="flex-shrink-0">
								{getIcon(notification.type)}
							</div>

							<div className="flex-1 min-w-0">
								<h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
									{notification.title}
								</h4>
								<p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
									{notification.message}
								</p>
							</div>

							<button
								onClick={() => dispatch(removeNotification(notification.id))}
								className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-white/50 dark:hover:bg-gray-800/50 transition-colors cursor-pointer"
							>
								<FiX className="w-4 h-4" />
								<span className="sr-only">Dismiss</span>
							</button>
						</div>

						{/* Progress bar for notifications with duration */}
						{notification.duration && (
							<motion.div
								className="absolute bottom-0 start-0 h-1 bg-gray-900/20 dark:bg-white/20 rounded-b-lg"
								initial={{ width: '100%' }}
								animate={{ width: '0%' }}
								transition={{ duration: notification.duration / 1000, ease: 'linear' }}
							/>
						)}
					</motion.div>
				))}
			</AnimatePresence>
		</div>
	);
};

export default NotificationSystem;